import React, { useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listFrequencies } from "../services/teacher";
import { listClassrooms } from "../services/classroom";
import { listGradebooks } from "../services/gradebook";
import { levelKey } from "../utils/classesLabels";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);

const classificationOf = (avg) => {
  if (avg >= 14) return { label: "classificacao.good", cls: "bg-success-focus text-success-main" };
  if (avg >= 10) return { label: "classificacao.sufficient", cls: "bg-warning-focus text-warning-main" };
  return { label: "classificacao.insufficient", cls: "bg-danger-focus text-danger-main" };
};

const RendimentoPedagogicoLayer = () => {
  const { t } = useTranslation();

  const [year, setYear] = useState(currentYear);
  const [frequencies, setFrequencies] = useState([]);
  const [levelId, setLevelId] = useState("");
  const [classrooms, setClassrooms] = useState([]);
  const [classId, setClassId] = useState("");

  const [gradebooks, setGradebooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedStudentId, setExpandedStudentId] = useState(null);

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
  }, []);

  useEffect(() => {
    listClassrooms(year).then((resp) => setClassrooms(resp.data || [])).catch(() => setClassrooms([]));
    setClassId("");
  }, [year]);

  const classOptions = useMemo(() => classrooms.filter((c) => !levelId || c.educationLevel === levelId), [classrooms, levelId]);

  useEffect(() => {
    if (!classId) {
      setGradebooks([]);
      return;
    }
    setLoading(true);
    listGradebooks({ classId })
      .then((resp) => setGradebooks((resp.data || []).filter((gb) => gb.status === "FINALIZED" || gb.status === "PUBLISHED")))
      .catch(() => toast.error(t("rendimento.error.load")))
      .finally(() => setLoading(false));
  }, [classId, t]);

  const studentPerformance = useMemo(() => {
    const byStudent = new Map();
    for (const gb of gradebooks) {
      for (const g of gb.grades || []) {
        if (!g.student?.id || g.grade === null || g.grade === undefined || g.grade === "") continue;
        const gradeNum = Number(g.grade);
        if (Number.isNaN(gradeNum)) continue;
        if (!byStudent.has(g.student.id)) {
          byStudent.set(g.student.id, { studentId: g.student.id, studentName: g.student.name, entries: [] });
        }
        byStudent.get(g.student.id).entries.push({
          subjectName: gb.subject?.name || "-",
          evaluationName: gb.evaluation?.name || "-",
          grade: gradeNum,
        });
      }
    }
    return Array.from(byStudent.values())
      .map((s) => ({
        ...s,
        average: s.entries.reduce((sum, e) => sum + e.grade, 0) / s.entries.length,
      }))
      .sort((a, b) => a.studentName.localeCompare(b.studentName, "pt"));
  }, [gradebooks]);

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='row gy-3 align-items-end'>
              <div className='col-sm-3'>
                <label className='form-label'>{t("turma.field.academicYear")}</label>
                <select className='form-select' value={year} onChange={(e) => setYear(Number(e.target.value))}>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-4'>
                <label className='form-label'>{t(levelKey("turma.field.educationLevel"))}</label>
                <select className='form-select' value={levelId} onChange={(e) => setLevelId(e.target.value)}>
                  <option value=''>{t(levelKey("disciplina.filter.allLevels"))}</option>
                  {frequencies.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-5'>
                <label className='form-label'>{t("turma.col.className")}</label>
                <select className='form-select' value={classId} onChange={(e) => setClassId(e.target.value)}>
                  <option value=''>{t("rendimento.field.class.placeholder")}</option>
                  {classOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.className}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!classId ? (
        <div className='col-12'>
          <div className='alert alert-info mb-0'>{t("rendimento.hint.selectClass")}</div>
        </div>
      ) : (
        <div className='col-12'>
          <div className='card radius-12'>
            <div className='card-body'>
              {loading ? (
                <div className='text-center py-40'>{t("global.loading")}</div>
              ) : studentPerformance.length === 0 ? (
                <div className='alert alert-info mb-0'>{t("rendimento.hint.noGrades")}</div>
              ) : (
                <div className='table-responsive'>
                  <table className='table bordered-table mb-0'>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }} />
                        <th>{t("presenca.col.name")}</th>
                        <th>{t("rendimento.col.subjectsCount")}</th>
                        <th>{t("rendimento.col.average")}</th>
                        <th>{t("rendimento.col.classification")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentPerformance.map((s) => {
                        const classification = classificationOf(s.average);
                        const isExpanded = expandedStudentId === s.studentId;
                        return (
                          <React.Fragment key={s.studentId}>
                            <tr style={{ cursor: "pointer" }} onClick={() => setExpandedStudentId(isExpanded ? null : s.studentId)}>
                              <td>
                                <i className={`ri-arrow-${isExpanded ? "down" : "right"}-s-line`} />
                              </td>
                              <td>{s.studentName}</td>
                              <td>{s.entries.length}</td>
                              <td>{s.average.toFixed(1)}</td>
                              <td>
                                <span className={`badge ${classification.cls}`}>{t(classification.label)}</span>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={5} className='bg-neutral-50'>
                                  <table className='table table-sm mb-0'>
                                    <thead>
                                      <tr>
                                        <th>{t("disciplina.field.subjectName")}</th>
                                        <th>{t("avaliacao.field.name")}</th>
                                        <th>{t("nota.col.grade")}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {s.entries.map((e, idx) => (
                                        <tr key={idx}>
                                          <td>{e.subjectName}</td>
                                          <td>{e.evaluationName}</td>
                                          <td>{e.grade}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RendimentoPedagogicoLayer;
